import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useModalConfirm,
  usePassengersActions,
  usePassengersBulkActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  ConfirmResult,
  createSuccessResult,
  errorAndToastSimpleResult,
  IPassengerDto,
  IPassengersSendMessage,
  ISearchCriteria,
  PassengerEntity,
  SimpleResult,
  SortEnumAPI,
} from "@ryde/shared";

import {
  BulkPassengersMessageFormFieldsEnum,
  IUseBulkPassengersMessageProps,
  RIDES_PARTICIPATION_PREVIEW_DEBOUNCE_MS,
} from "./bulk-passengers-message.models";
import { excludeInactivePassengers } from "./bulk-passengers-message.utils";
import { useBulkPassengersMessageValidation } from "./use-bulk-passengers-message.validation";

export const useBulkPassengersMessageDrawer = ({
  selectedPassengersIds,
  hasRecipientsDerivedFromRides,
}: IUseBulkPassengersMessageProps) => {
  const { t } = useTranslation();
  const { searchPassengerByCriteria } = usePassengersActions();
  const [passengers, setPassengers] = useState<IPassengerDto[]>([]);
  const [isLoading, startTransition] = useTransition();
  const { bulkSendMessageToPassengers, fetchRidesParticipationRecipientIds } =
    usePassengersBulkActions();
  const { bulkPassengersMessageResolver } =
    useBulkPassengersMessageValidation();

  const resolver = useYupValidationResolver(
    bulkPassengersMessageResolver,
    "passenger",
  );

  const { confirmDialogResult } = useModalConfirm();

  const methods = useForm<IPassengersSendMessage>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      subject: "",
      preview: "",
      message: "",
      isEmail: false,
      isPush: true,
      isSms: false,
      lastRideWithinDays: null,
    },
    resolver: resolver,
  });

  const {
    watch,
    getValues,
    formState: { errors, isValid },
  } = methods;

  const isSendViaEmailValueWatch = watch(
    BulkPassengersMessageFormFieldsEnum.isEmail,
  );

  const isSendViaSmsValueWatch = watch(
    BulkPassengersMessageFormFieldsEnum.isSms,
  );

  const isSendViaPushValueWatch = watch(
    BulkPassengersMessageFormFieldsEnum.isPush,
  );

  const lastRideWithinDaysValueWatch = watch(
    BulkPassengersMessageFormFieldsEnum.lastRideWithinDays,
  );

  // `undefined` = filter off or preview unavailable -> show the whole selection.
  const [qualifyingPassengerIds, setQualifyingPassengerIds] = useState<
    number[] | undefined
  >(undefined);
  const [isPreviewingRecipients, setIsPreviewingRecipients] = useState(false);
  /** Bumped on every filter/selection change so a stale in-flight preview cannot write state. */
  const previewGenerationRef = useRef(0);

  const recipients = useMemo(() => {
    if (!qualifyingPassengerIds) {
      return passengers;
    }
    const qualifyingIdSet = new Set(qualifyingPassengerIds);

    return passengers.filter(passenger => qualifyingIdSet.has(passenger.id));
  }, [passengers, qualifyingPassengerIds]);

  const isSaveDisabled = useMemo(() => {
    const isAtLeastOneMethodChosen =
      isSendViaEmailValueWatch ||
      isSendViaSmsValueWatch ||
      isSendViaPushValueWatch;

    // An empty recipients list covers both ways it can empty out: nothing resolved from the selection
    // (or nobody active, on the rides flow), and the participation filter leaving nobody. Either way
    // the send could only come back as an error.
    return (
      isLoading || !isValid || !isAtLeastOneMethodChosen || !recipients.length
    );
  }, [
    isSendViaEmailValueWatch,
    isSendViaPushValueWatch,
    isSendViaSmsValueWatch,
    isLoading,
    isValid,
    recipients.length,
  ]);

  const onSaveClick = useEvent(async (): Promise<void> => {
    if (!isValid || !recipients.length) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.SEND_MESSAGES_TO_PASSENGERS_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const { lastRideWithinDays, ...FormFields } = getValues();

    bulkSendMessageToPassengers({
      ...FormFields,
      // An empty field must reach the API as an absent key, not as null — the filter is opt-in.
      ...(lastRideWithinDays ? { lastRideWithinDays } : {}),
      // `passengers`, not `recipients`: the API re-applies the participation filter and stays the
      // authority on it, so a stale or failed preview must never shrink the real send. What is dropped
      // here is only what no preview can put back — ids that resolved to no passenger, and inactive
      // records on the rides flow.
      to: passengers.map(({ id }) => id),
    });
  });

  const fetchPassengersOnInit = useEvent(
    async (passengersIds: number[]): Promise<SimpleResult<void>> => {
      const body: ISearchCriteria<PassengerEntity> = {
        where: { id: { $in: passengersIds } },
        sort: SortEnumAPI.SortById,
      };
      const result = await searchPassengerByCriteria(body);

      if (result.success && result.data) {
        setPassengers(
          hasRecipientsDerivedFromRides
            ? excludeInactivePassengers(result.data)
            : result.data,
        );
      } else {
        errorAndToastSimpleResult(result, t);
      }

      return createSuccessResult();
    },
  );

  useEffect(() => {
    if (selectedPassengersIds?.length) {
      startTransition(() => {
        fetchPassengersOnInit(selectedPassengersIds);
      });
    }
  }, [fetchPassengersOnInit, selectedPassengersIds]);

  const previewRecipients = useEvent(
    async (
      days: number,
      passengersIds: number[],
      generation: number,
    ): Promise<void> => {
      setIsPreviewingRecipients(true);
      const qualifyingIds = await fetchRidesParticipationRecipientIds({
        ids: passengersIds,
        lastRideWithinDays: days,
      });

      // Latest request wins. A newer keystroke — or a cleared field — already superseded this one, and
      // applying its answer would re-narrow the list to a filter the admin is no longer looking at.
      if (generation !== previewGenerationRef.current) {
        return;
      }

      setQualifyingPassengerIds(qualifyingIds);
      setIsPreviewingRecipients(false);
    },
  );

  const previewRecipientsDebounced = useMemo(
    () =>
      debounce(previewRecipients, RIDES_PARTICIPATION_PREVIEW_DEBOUNCE_MS, {
        trailing: true,
      }),
    [previewRecipients],
  );

  useEffect(() => {
    // Every change invalidates whatever is in flight: `debounce.cancel()` only drops calls that have
    // not started yet, so without this an older response could land last and win.
    previewGenerationRef.current += 1;
    const generation = previewGenerationRef.current;

    // A cleared or invalid field means "no filter" — drop the narrowing without hitting the API.
    if (!lastRideWithinDaysValueWatch || !selectedPassengersIds?.length) {
      previewRecipientsDebounced.cancel();
      setQualifyingPassengerIds(undefined);
      setIsPreviewingRecipients(false);
      return;
    }

    previewRecipientsDebounced(
      lastRideWithinDaysValueWatch,
      selectedPassengersIds,
      generation,
    );

    return () => {
      previewRecipientsDebounced.cancel();
      // Unmount or next change: nothing still in flight may write state any more.
      previewGenerationRef.current += 1;
    };
  }, [
    lastRideWithinDaysValueWatch,
    previewRecipientsDebounced,
    selectedPassengersIds,
  ]);

  return {
    methods,
    errors,
    isSaveDisabled,
    passengers,
    recipients,
    isPreviewingRecipients,
    isLoading,
    onSaveClick,
  };
};
